import styled from 'styled-components';
import { motion } from 'framer-motion';

export const ShowcaseContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #053145 0%, #032837 100%);
  color: white;
  overflow-x: hidden;
`;

export const Header = styled.header`
  padding: 2rem 5%;
  background: rgba(0, 0, 0, 0.3);
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

export const LogoImage = styled.img`
  max-width: 200px;
  height: auto;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
`;

export const Logo = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #dfae46;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const Tagline = styled.p`
  font-size: 1.6rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const Section = styled.section`
  padding: 4rem 5%;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem 5%;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
  color: #dfae46;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: #dfae46;
    margin: 1rem auto 0;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(223, 174, 70, 0.4);
  }
`;

export const SectionContent = styled.div`
  font-size: 1.6rem;
  line-height: 1.8;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul {
    list-style: none;
    padding-left: 0;
  }
  
  li {
    margin-bottom: 1rem;
    padding-left: 2rem;
    position: relative;
    
    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #dfae46;
      font-weight: bold;
      font-size: 1.7rem;
    }
  }
`;

export const VideoContainer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 4rem 3rem;
  text-align: center;
  margin: 2rem 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(223, 174, 70, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    min-height: 250px;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
  
  .video-placeholder {
    width: 100%;
  }
  
  p {
    font-size: 1.3rem;
    opacity: 0.9;
  }
`;

export const DifferentialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  grid-auto-rows: 1fr;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DifferentialCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(223, 174, 70, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  height: 100%;
  
  @media (max-width: 768px) {
    min-height: 200px;
    padding: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 48px rgba(223, 174, 70, 0.2);
    border-color: rgba(223, 174, 70, 0.5);
  }
  
  h3 {
    font-size: 1.7rem;
    margin-bottom: 1rem;
    color: #dfae46;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  
  p {
    font-size: 1.4rem;
    line-height: 1.6;
    flex: 1;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

export const PricingSection = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 3rem;
  margin: 2rem 0;
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 2rem -4%;
  }
`;

export const PricingTable = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(223, 174, 70, 0.2);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    border-color: rgba(223, 174, 70, 0.5);
    box-shadow: 
      0 15px 40px rgba(0, 0, 0, 0.5),
      0 0 20px rgba(223, 174, 70, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  
  h3 {
    font-size: 1.9rem;
    margin-bottom: 1rem;
    color: #dfae46;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 1.4rem;
    }
  }
  
  .price {
    font-size: 4.5rem;
    font-weight: 900;
    margin: 1.5rem 0;
    color: #dfae46;
    text-shadow: 0 4px 8px rgba(0, 0, 0, 0.5), 0 0 20px rgba(223, 174, 70, 0.3);
    letter-spacing: -1px;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  .description {
    font-size: 1.3rem;
    opacity: 0.9;
    font-weight: 500;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

export const FAQContainer = styled.div`
  margin-top: 2rem;
`;

export const FAQItem = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 
      0 12px 35px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(223, 174, 70, 0.3);
  }
  
  h3 {
    font-size: 1.6rem;
    margin-bottom: 1rem;
    color: #dfae46;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
  
  p {
    font-size: 1.4rem;
    line-height: 1.6;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

export const HighlightBox = styled.div`
  background: rgba(223, 174, 70, 0.15);
  border-left: 4px solid #dfae46;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  p {
    margin: 0.5rem 0;
  }
`;

export const StepsContainer = styled.div`
  margin-top: 2rem;
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StepCenterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  width: 100%;
  
  > div {
    width: calc(50% - 0.5rem);
    
    @media (max-width: 768px) {
      width: 100%;
    }
  }
`;

export const Step = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 
      0 12px 35px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(223, 174, 70, 0.3);
  }
  
  .step-number {
    background: linear-gradient(135deg, #dfae46 0%, #f0c674 100%);
    color: #053145;
    font-size: 1.8rem;
    font-weight: 700;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 
      0 4px 15px rgba(223, 174, 70, 0.4),
      inset 0 2px 0 rgba(255, 255, 255, 0.3);
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
      width: 45px;
      height: 45px;
    }
  }
  
  .step-content {
    flex: 1;
    font-size: 1.3rem;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

export const ModernPriceCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  margin: 2rem auto;
  max-width: 500px;
  border: 2px solid rgba(223, 174, 70, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 48px rgba(223, 174, 70, 0.3);
    border-color: #dfae46;
  }
`;

export const BenefitsCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2.5rem;
  width: fit-content;
  margin: 0 auto;
  border: 1px solid rgba(223, 174, 70, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
  
  ul {
    list-style: none;
    padding-left: 0;
    text-align: left;
  }
  
  li {
    margin-bottom: 1.2rem;
    padding-left: 2.5rem;
    position: relative;
    font-size: 1.6rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
      padding-left: 2rem;
    }
    
    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #dfae46;
      font-weight: bold;
      font-size: 1.9rem;
      
      @media (max-width: 768px) {
        font-size: 1.4rem;
      }
    }
  }
`;

export const Footer = styled.footer`
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(5, 49, 69, 0.6) 100%);
  padding: 4rem 5%;
  margin-top: 4rem;
  border-top: 2px solid rgba(223, 174, 70, 0.3);
`;

export const FooterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

export const ContactButton = styled(motion.button)`
  background: linear-gradient(135deg, #dfae46 0%, #f0c674 100%);
  color: #053145;
  font-size: 1.7rem;
  font-weight: 700;
  padding: 1.3rem 4rem;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(223, 174, 70, 0.4);
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 1rem 2.5rem;
  }
  
  &:hover {
    box-shadow: 0 6px 30px rgba(223, 174, 70, 0.6);
  }
`;
